import PrintRounded from '@mui/icons-material/PrintRounded';
import { Button, IconButton, Tooltip } from '@mui/material';
import { EmailSend } from '../email/EmailSend';
import { useShopLogo } from '../../lib/images';
import { useShop } from '../../shop/ShopProvider';
import { shopHeader } from '../sales/print/builders';
import { A4Document, type PrintDoc } from '../sales/print/documents';
import { usePrint } from '../sales/print/usePrint';
import type { WhatsappDocumentType } from './api';
import type { MessageVars } from './messages';
import { WhatsappSend, type WhatsappTarget } from './WhatsappSend';

/**
 * The Print, WhatsApp and Email buttons of a document in its A4 layout (WhatsApp and Email send the
 * number, contact and PDF in one go, each only where its plugin is on for this kind of document).
 * Pages with a print view of their own pass `print={false}`.
 */
export function WhatsappA4({
  type,
  id,
  number,
  contact,
  vars,
  doc,
  variant,
  print: printable = true,
}: {
  type: WhatsappDocumentType;
  id: string;
  number: string;
  contact: WhatsappTarget['contact'];
  vars?: MessageVars;
  doc: () => PrintDoc;
  variant?: 'button' | 'icon';
  print?: boolean;
}) {
  const { settings } = useShop();
  const logo = useShopLogo();
  const { print, element } = usePrint();
  const render = () => <A4Document shop={shopHeader(settings, undefined, logo)} doc={doc()} />;
  const target: WhatsappTarget = {
    documentType: type,
    documentId: id,
    documentRef: number,
    contact,
    vars: { number, name: contact?.name, ...vars },
    pdf: {
      fileName: `${number}.pdf`,
      paper: 'a4',
      render,
    },
  };
  return (
    <>
      {printable &&
        (variant === 'icon' ? (
          <Tooltip title="Print">
            <IconButton aria-label="Print" onClick={() => print('a4', render())}>
              <PrintRounded />
            </IconButton>
          </Tooltip>
        ) : (
          <Button
            variant="outlined"
            startIcon={<PrintRounded />}
            onClick={() => print('a4', render())}
          >
            Print
          </Button>
        ))}
      {element}
      <WhatsappSend variant={variant} target={target} />
      <EmailSend variant={variant} target={target} />
    </>
  );
}
